import React, { Fragment, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, PencilIcon, CheckIcon, XMarkIcon as CancelIcon } from '@heroicons/react/24/outline';
import { NoteData, ProfileStatusMaster, SbCurrentUser, SbRecipientModel, SubmissionBoardModel } from './SbModel';
import SbAddNote from './SbAddNote';
import submissionBoardService from '../../services/SubmissionBoardService';
import Button from './Button';
import { XIcon } from 'lucide-react';

export interface SbShowNotesProps {
  currentUser: SbCurrentUser;
  sb_recipient?: SbRecipientModel;
  profileUser: any;
  from?: string;
  submissionBoardModel?: SubmissionBoardModel;
  project?: any;
  isSbLocked:boolean;
  dialogCloseHandler?: (open: boolean) => void;
  onNoteCountChange?: (count: number) => void;
  onProfileDataRefresh?: () => void;
}


export default function SbShowNotes({
  currentUser,
  sb_recipient,
  profileUser,
  from,
  project,
  submissionBoardModel,
  isSbLocked,
  dialogCloseHandler,
  onNoteCountChange,
  onProfileDataRefresh,
}: SbShowNotesProps) {
  const [selectedSubmissionBoard, setSelectedSubmissionBoard] =
    useState<SubmissionBoardModel | null>(null);
  const [showDialog, setShowDialog] = useState(true);
  const [noteTexts, setNoteTexts] = useState<NoteData[]>([]);
  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  // Function to calculate and update note count
  const calculateNoteCount = (notes: NoteData[]) => {
    if (onNoteCountChange) {
      onNoteCountChange(notes.length);
    }
  };
  
  useEffect(() => {
    setSelectedSubmissionBoard(submissionBoardModel);
  }, [submissionBoardModel]);


  useEffect(() => {
    let _noteTexts: NoteData[] = [];
    if (profileUser?.sb_profiles && profileUser?.sb_profiles.length > 0) {
      profileUser?.sb_profiles.map((item1) => {
        // Handle new note_texts format with user info and timestamps
        if (item1.note_texts && Array.isArray(item1.note_texts)) {
          _noteTexts.push(...item1.note_texts);
        }
        
      });
      
      if (_noteTexts.length > 0) {
        setNoteTexts(_noteTexts);
      }
    }
    
    // Notify parent about initial note count
    calculateNoteCount(_noteTexts);
  }, [profileUser, onNoteCountChange]);

  const isMobileScreen = useMedia('(max-width: 1024px)', false);

  const onCloseDialog = () => {
    if (dialogCloseHandler) {
      dialogCloseHandler(false);
    }
    setShowDialog(false);
  };
  // Escape close popup start
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showDialog && !showAddNoteModal) {
        onCloseDialog();
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => {
      window.removeEventListener('keydown', handleEscKey);
    };
  }, [onCloseDialog]);
  // Escape close popup end
  // Add Notes button → close parent & open child
  const handleAddNoteClick = () => {
    setShowAddNoteModal(true);
  };

  // Child close → reopen parent
  const handleCloseAddNoteModal = () => {
    setShowAddNoteModal(false);
  };

  // const handleNoteSubmit = (note: string) => {
  //   const newNote: NoteData = {
  //     id: Date.now(),
  //     note_text: note,
  //     user_name: currentUser?.first_name || currentUser?.email || 'Current User',
  //     created_at:
  //       new Date().toLocaleDateString('en-GB', {
  //         day: '2-digit',
  //         month: '2-digit',
  //         year: 'numeric',
  //       }) +
  //       ' • ' +
  //       new Date().toLocaleTimeString('en-US', {
  //         hour: 'numeric',
  //         minute: '2-digit',
  //         hour12: true,
  //       }),
  //     user_id: currentUser.id
  //   };
    
  //   const newNoteTexts = [...noteTexts, newNote];
  //   setNoteTexts(newNoteTexts);
  //   setShowAddNoteModal(false);
  //   setShowDialog(true);
    
  //   // Notify parent about updated note count
  //   calculateNoteCount(newNoteTexts);
    
  //   // Trigger profile data refresh to get latest data from API
  //   if (onProfileDataRefresh) {
  //     onProfileDataRefresh();
  //   }

  //   console.log('New note added:', note);
  // };
  // Start editing a note
  const handleEditNote = (noteData: NoteData) => {
    setEditingNoteId(noteData.id);
    setEditingNoteText(noteData.note_text);
  };
  // Cancel editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditingNoteText('');
  };
  // Save edited note
  const handleSaveEdit = async (noteId: number) => {
    if (!editingNoteText.trim()) return;
    setIsUpdating(true);
    try {
      const response = await submissionBoardService.updateNote(
        project.uuid,
        selectedSubmissionBoard.id,
        noteId,
        profileUser.sb_profiles?.[0]?.sb_profile_id,
        editingNoteText.trim(),
        sb_recipient && sb_recipient.key_token ? sb_recipient.key_token : '',
        sb_recipient && sb_recipient.otp_valid ? sb_recipient.otp_valid : ''
      );
      if (response) {
        // Update local state
        const updatedNotes = noteTexts.map(note => 
          note.id === noteId 
            ? { ...note, note_text: editingNoteText.trim() }
            : note
        );
        setNoteTexts(updatedNotes);
        // Reset editing state
        setEditingNoteId(null);
        setEditingNoteText('');
        // Trigger profile data refresh to get latest data from API
        if (onProfileDataRefresh) {
          onProfileDataRefresh();
        }
        console.log('Note updated successfully');
      }
    } catch (error) {
      console.error('Error updating note:', error);
      // You can add a toast notification here for error handling
    } finally {
      setIsUpdating(false);
    }
  };

  const getNoteName = (user_id: number, sb_recipient_id: any) => {
    if (sb_recipient_id && sb_recipient_id != null) {
      if (selectedSubmissionBoard?.recipients && selectedSubmissionBoard?.recipients.length > 0) {
        const resp = selectedSubmissionBoard?.recipients.find(
          (item) => item.sb_recipient_id == sb_recipient_id
        );
        if (resp) {
          return resp.email;
        }
      }
    } else if (user_id && user_id != null) {
      if (
        selectedSubmissionBoard?.coordinators &&
        selectedSubmissionBoard?.coordinators.length > 0
      ) {
        const resp = selectedSubmissionBoard?.coordinators.find(
          (item) => item.user_id == user_id
        );
        if (resp) {
          return resp.name;
        }
      }
      if (selectedSubmissionBoard?.recipients && selectedSubmissionBoard?.recipients.length > 0) {
        const resp = selectedSubmissionBoard?.recipients.find((item) => item.id == user_id);
        if (resp) {
          return resp.name;
        }
      }
    }
    return "";
  };
  // Check if current user can edit this note
  const canEditNote = (noteData: NoteData) => {
    if (isProfileHidden()) {
      return false;
    }
    return (currentUser != undefined && noteData.user_id === currentUser.id) ||
      (sb_recipient != undefined && sb_recipient.id === noteData.sb_recipient_id);
  };
  const isProfileHidden = () => {
    return profileUser?.sb_profiles[0]?.profile_status === ProfileStatusMaster.Rejected;
  };

  return (
    <Transition.Root show={showDialog} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onCloseDialog}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-customGray" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-left">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative flex flex-col h-full overflow-hidden bg-white rounded-md shadow-xl-0-19-40-8 ">
                {/* <div className="flex items-center justify-between px-4 py-2">
                  <h2 className="text-lg font-medium text-gray-900">Profile Notes</h2>
                  <button
                    id={'show_note_close_model_id'}
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-md hover:bg-gray-100"
                    onClick={handleHideDialog}
                  >
                    <span className="sr-only">Close menu</span>
                    <XMarkIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
                  </button>
                </div> */}
                <div
                  className="bg-white rounded-lg h-full shadow-xl flex flex-col overflow-visible gap-2-1"
                  style={{
                    height: '80vh',
                    maxHeight: '31.25rem',
                    width: '80vw',
                    maxWidth: '31.25rem',
                  }}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-4 sm:px-6 py-5">
                    <h2 className="text-lg font-medium text-black">Profile Notes</h2>
                    <button
                      className="flex items-center justify-center text-gray-700 hover:text-gray-600 transition-colors h-6 w-6"
                      onClick={onCloseDialog}
                    >
                      <XIcon className="w-6 h-6" />
                    </button>
                  </div>
                  {selectedSubmissionBoard && (
                    <>
                      <div className="pb-10 px-4 sm:px-10 h-full " style={{}}>
                        <div className="gap-5 h-full flex flex-col">
                          {/* Profile Info */}
                          <div className="flex flex-none flex-row gap-2-1 items-center ">
                            <div className="flex-1 flex flex-row min-w-0">
                              {/* <div
                                className="rounded-lg-10"
                                style={{
                                  width: '3.125rem',
                                  height: '3.125rem',
                                }}
                              >
                                <img
                                  src={profileUser?.croppedAvatarUrl || profileUser?.imageUrl || ''}
                                  alt={''}
                                  className="w-full h-full object-cover rounded-lg-10"
                                  onError={(e) => {}}
                                />
                              </div> */}
                              <div className="">
                                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                                  {(profileUser?.firstName ?? '') +
                                    ' ' +
                                    (profileUser?.lastName ?? '')}
                                </h3>
                                <p className="text-gray-600 text-sm">
                                  {selectedSubmissionBoard.title}
                                </p>
                              </div>
                            </div>
                            {!isSbLocked && (
                              <Button
                                className={`  ${isProfileHidden()
                                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    : ''
                                  }`}
                                disabled={isProfileHidden()}
                                id="add_note_button"
                                variant="primary"
                                // type="button"
                                // className="ml-4 px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm"
                                onClick={handleAddNoteClick}
                              >
                                Add Note
                              </Button>
                            )}
                          </div>
                          <hr className="flex-none" />

                          {/* Notes Content */}
                          <div className="flex-grow scrollable-content overflow-y-scroll gap-5 mb-20 px-2">
                            {noteTexts.length === 0 ? (
                              <div className="flex items-center justify-center h-32 text-gray-500 text-sm">
                                No notes available
                              </div>
                            ) : (
                              <>
                                {noteTexts.map((noteData, index) => (
                                  <div
                                    key={noteData.id || index}
                                    className="py-5 gap-2 border-b border-dashed border-gray-200 last:border-b-0"
                                  >
                                    {/* User Info and Timestamp with Edit Button */}
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2-1">
                                        <span className="text-base font-bold text-black">
                                          {getNoteName(noteData.user_id, noteData.sb_recipient_id)}
                                        </span>
                                        <span className="text-xs font-medium text-gray-400">
                                          {noteData.created_at}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {canEditNote(noteData) && editingNoteId !== noteData.id && (
                                          <Button
                                            id="edit_note_button"
                                            variant="secondary"
                                            onClick={() => handleEditNote(noteData)}
                                            className="p-1 hover:bg-gray-100 rounded"
                                          >
                                            <PencilIcon className="h-4 w-4 text-gray-500" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    {/* Note Content */}
                                    {editingNoteId === noteData.id ? (
                                      <div className="space-y-2">
                                        <textarea
                                          value={editingNoteText}
                                          onChange={(e) => setEditingNoteText(e.target.value)}
                                          className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          rows={3}
                                          placeholder="Edit your note..."
                                        />
                                        <div className="flex justify-end gap-2">
                                          <Button
                                            id="cancel_edit_note_button"
                                            variant="secondary"
                                            onClick={handleCancelEdit}
                                            // className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded-md flex items-center gap-1"
                                            disabled={isUpdating}
                                          >
                                            <CancelIcon className="h-4 w-4" />
                                            Cancel
                                          </Button>
                                          <Button
                                            id="save_edit_note_button"
                                            variant="primary"
                                            onClick={() => handleSaveEdit(noteData.id)}
                                            // className="px-3 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-md flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                            disabled={isUpdating || !editingNoteText.trim()}
                                          >
                                            <CheckIcon className="h-4 w-4" />
                                            {isUpdating ? 'Saving...' : 'Save'}
                                          </Button>
                                        </div>
                                      </div>
                                    ) : (
                                      <div
                                        className={`whitespace-pre-line text-base text-black leading-relaxed ${canEditNote(noteData) ? 'cursor-pointer hover:bg-gray-50 rounded p-2 -m-2 transition-colors' : ''}`}
                                        onDoubleClick={() => canEditNote(noteData) && handleEditNote(noteData)}
                                        title={canEditNote(noteData) ? 'Double-click to edit' : ''}
                                      >
                                        {noteData.note_text}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {/* Child Add Note Modal */}
                {showAddNoteModal && !isSbLocked && (
                  <SbAddNote
                    currentUser={currentUser}
                    sb_recipient={sb_recipient}
                    isOpen={showAddNoteModal}
                    onClose={handleCloseAddNoteModal}
                    projectId={project.uuid}
                    submissionBoardId={selectedSubmissionBoard.id}
                    isSbLocked={isSbLocked}
                    profileId={profileUser.sb_profiles?.[0]?.sb_profile_id}
                    profileName={
                      (profileUser?.firstName ?? '') + ' ' + (profileUser?.lastName ?? '')
                    }
                    profileImage={profileUser?.croppedAvatarUrl || profileUser?.imageUrl || ''}
                    submissionBoardName={selectedSubmissionBoard.title}
                    onNoteAdded={(note) => {
                      const newNote: NoteData = {
                        id: Date.now(),
                        note_text: note,
                        user_name: currentUser?.first_name || currentUser?.email || 'Current User',
                        created_at:
                          new Date().toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                          }) +
                          ' • ' +
                          new Date().toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true,
                          }),
                        user_id: currentUser ? currentUser.id : 0,
                        sb_recipient_id: currentUser ? 0 : sb_recipient.id,
                        note_role: '',
                      };

                      const updatedNoteTexts = [...noteTexts, newNote];
                      setNoteTexts(updatedNoteTexts);

                      // Notify parent about updated note count
                      if (onNoteCountChange) {
                        onNoteCountChange(updatedNoteTexts.length);
                      }

                      // Trigger profile data refresh to get latest data from API
                      if (onProfileDataRefresh) {
                        onProfileDataRefresh();
                      }
                    }}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}